/**
 * FormRenderer — given a FormDefinition + initial payload, renders all
 * fields and validates on submit. Used by:
 *   - SA preview page
 *   - Public admission form (Phase D)
 *   - Tenant Configuration Hub (Phase B step 3)
 *   - Anywhere a tenant-configured form needs to be shown
 *
 * State is held internally with useState; controlled mode is opt-in via
 * the `value` + `onChange` props. Validation runs on submit and (for
 * touched fields) onBlur — same UX rule as the BE validators module.
 */
import * as React from "react";
import { Upload, X } from "lucide-react";
import type {
  ConditionalRule,
  FieldValidationError,
  FileRef,
  FormDefinition,
  FormField,
} from "@eduviaos/shared/types";
import { Button } from "../components/button.js";
import { Checkbox } from "../components/checkbox.js";
import { Input } from "../components/input.js";
import { Label } from "../components/label.js";
import { RadioGroup, RadioGroupItem } from "../components/radio-group.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/select.js";
import { Textarea } from "../components/textarea.js";
import { cn } from "../lib/cn.js";

// â”€â”€ Conditional rule evaluator (mirrors validators.py) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function evaluateRule(rule: ConditionalRule, payload: Record<string, unknown>): boolean {
  const other = payload[rule.field_key];
  switch (rule.operator) {
    case "exists":
      return (
        other !== undefined &&
        other !== null &&
        other !== "" &&
        !(Array.isArray(other) && other.length === 0)
      );
    case "not_exists":
      return (
        other === undefined ||
        other === null ||
        other === "" ||
        (Array.isArray(other) && other.length === 0)
      );
    case "eq":
      return other === rule.value;
    case "neq":
      return other !== rule.value;
    case "in":
      return Array.isArray(rule.value) && rule.value.includes(other as never);
    case "not_in":
      return Array.isArray(rule.value) && !rule.value.includes(other as never);
    case "gt":
      return Number(other) > Number(rule.value);
    case "lt":
      return Number(other) < Number(rule.value);
    default:
      return false;
  }
}

// â”€â”€ Field renderer (all 17 field types in one switch) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface FieldRendererProps {
  field: FormField;
  value: unknown;
  files: FileRef[];
  onChange: (value: unknown) => void;
  onFilesChange: (files: FileRef[]) => void;
  error: string | null;
  disabled?: boolean;
}

function FieldRenderer({
  field,
  value,
  files,
  onChange,
  onFilesChange,
  error,
  disabled,
}: FieldRendererProps) {
  const id = `f-${field.key}`;
  const v = field.validation ?? {};
  const opts = v.options ?? [];
  const placeholder = field.placeholder ?? undefined;
  const inputCls = cn(error && "border-destructive focus-visible:ring-destructive/40");

  switch (field.field_type) {
    case "text":
      return (
        <Input
          id={id}
          type="text"
          value={(value as string) ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputCls}
        />
      );

    case "email":
      return (
        <Input
          id={id}
          type="email"
          value={(value as string) ?? ""}
          placeholder={placeholder ?? "name@example.com"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputCls}
        />
      );

    case "phone":
      return (
        <Input
          id={id}
          type="tel"
          value={(value as string) ?? ""}
          placeholder={placeholder ?? "+91 9XXXXXXXXX"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputCls}
        />
      );

    case "number":
    case "currency":
      return (
        <div className="relative">
          {field.field_type === "currency" && (
            <span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 text-sm">
              â‚¹
            </span>
          )}
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            value={value === undefined || value === null ? "" : String(value)}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            disabled={disabled}
            className={cn(inputCls, field.field_type === "currency" && "pl-7")}
            min={v.min_value ?? undefined}
            max={v.max_value ?? undefined}
          />
        </div>
      );

    case "date":
      return (
        <Input
          id={id}
          type="date"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={inputCls}
        />
      );

    case "datetime":
      return (
        <Input
          id={id}
          type="datetime-local"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={inputCls}
        />
      );

    case "select":
      return (
        <Select
          value={(value as string) ?? ""}
          onValueChange={(val) => onChange(val)}
          disabled={disabled}
        >
          <SelectTrigger id={id} className={inputCls}>
            <SelectValue placeholder={placeholder ?? "Select..."} />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "multi_select": {
      const arr = (value as string[] | undefined) ?? [];
      return (
        <div className="border-input grid gap-2 rounded-md border p-3">
          {opts.map((o) => {
            const checked = arr.includes(o.value);
            return (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(state) => {
                    if (state === true) onChange([...arr, o.value]);
                    else onChange(arr.filter((x) => x !== o.value));
                  }}
                  disabled={disabled}
                />
                {o.label}
              </label>
            );
          })}
        </div>
      );
    }

    case "radio":
      return (
        <RadioGroup
          value={(value as string) ?? ""}
          onValueChange={(val) => onChange(val)}
          disabled={disabled}
        >
          {opts.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value={o.value} />
              {o.label}
            </label>
          ))}
        </RadioGroup>
      );

    case "checkbox": {
      const arr = (value as string[] | undefined) ?? [];
      return (
        <div className="grid gap-2">
          {opts.map((o) => {
            const checked = arr.includes(o.value);
            return (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(state) => {
                    if (state === true) onChange([...arr, o.value]);
                    else onChange(arr.filter((x) => x !== o.value));
                  }}
                  disabled={disabled}
                />
                {o.label}
              </label>
            );
          })}
        </div>
      );
    }

    case "textarea":
      return (
        <Textarea
          id={id}
          value={(value as string) ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
          className={inputCls}
        />
      );

    case "address": {
      const addr = (value as Record<string, string> | undefined) ?? {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "IN",
      };
      const set = (k: string, v: string) => onChange({ ...addr, [k]: v });
      return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input
            placeholder="Address line 1"
            value={addr.line1 ?? ""}
            onChange={(e) => set("line1", e.target.value)}
            className="sm:col-span-2"
            disabled={disabled}
          />
          <Input
            placeholder="Address line 2 (optional)"
            value={addr.line2 ?? ""}
            onChange={(e) => set("line2", e.target.value)}
            className="sm:col-span-2"
            disabled={disabled}
          />
          <Input
            placeholder="City"
            value={addr.city ?? ""}
            onChange={(e) => set("city", e.target.value)}
            disabled={disabled}
          />
          <Input
            placeholder="State"
            value={addr.state ?? ""}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
          />
          <Input
            placeholder="Pincode"
            value={addr.pincode ?? ""}
            onChange={(e) => set("pincode", e.target.value)}
            disabled={disabled}
          />
          <Input
            placeholder="Country"
            value={addr.country ?? "IN"}
            onChange={(e) => set("country", e.target.value)}
            disabled={disabled}
          />
        </div>
      );
    }

    case "file_upload": {
      const myFiles = files.filter((f) => f.field_key === field.key);
      const accept = (v.mime_types ?? []).join(",");
      const onSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = Array.from(e.target.files ?? []);
        if (chosen.length === 0) return;
        // For Phase A1, we do not stream files to a real DocumentVault
        // (Phase 6). Synthesize FileRef with a base64-ish placeholder
        // file_id; submission acceptance still validates mime + size.
        const newRefs: FileRef[] = chosen.map((f) => ({
          field_key: field.key,
          file_id: `local-${crypto.randomUUID()}`,
          filename: f.name,
          mime_type: f.type || "application/octet-stream",
          size_bytes: f.size,
        }));
        const next = v.multiple_files
          ? [...files.filter((f) => f.field_key !== field.key || true), ...newRefs]
          : [...files.filter((f) => f.field_key !== field.key), ...newRefs];
        onFilesChange(next);
        e.target.value = "";
      };
      const remove = (id: string) => onFilesChange(files.filter((f) => f.file_id !== id));
      return (
        <div className="grid gap-2">
          <label
            className={cn(
              "border-input bg-muted/30 flex cursor-pointer items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm",
              error && "border-destructive",
            )}
          >
            <span className="text-muted-foreground flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {placeholder ?? "Click to upload"}
            </span>
            <span className="text-muted-foreground text-xs">
              {v.mime_types?.join(", ") ?? "any"}
              {v.max_file_size_mb ? ` · ≤${v.max_file_size_mb} MB` : ""}
            </span>
            <input
              type="file"
              accept={accept || undefined}
              multiple={!!v.multiple_files}
              onChange={onSelect}
              className="sr-only"
              disabled={disabled}
            />
          </label>
          {myFiles.map((f) => (
            <div
              key={f.file_id}
              className="border-input bg-background flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
            >
              <span className="truncate">
                {f.filename}{" "}
                <span className="text-muted-foreground text-xs">
                  ({Math.round(f.size_bytes / 1024)} KB)
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(f.file_id)}
                disabled={disabled}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      );
    }

    case "signature":
      return (
        <Textarea
          id={id}
          value={(value as string) ?? ""}
          placeholder={placeholder ?? "Type your name to sign (full canvas in next release)"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={2}
          className={cn(inputCls, "font-mono")}
        />
      );

    case "section_header":
      return (
        <h3 className="border-border text-foreground border-b pb-2 text-sm font-semibold">
          {field.label}
        </h3>
      );

    case "help_text":
      return <p className="text-muted-foreground text-sm">{field.help_text ?? field.label}</p>;
  }

  return null;
}

// â”€â”€ Public component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface FormRendererProps {
  form: FormDefinition;
  value?: Record<string, unknown>;
  files?: FileRef[];
  onChange?: (value: Record<string, unknown>) => void;
  onFilesChange?: (files: FileRef[]) => void;
  /** Server-returned errors after submit attempt. */
  serverErrors?: FieldValidationError[];
  onSubmit: (payload: Record<string, unknown>, files: FileRef[]) => void | Promise<void>;
  submitting?: boolean;
  disabled?: boolean;
  showSubmit?: boolean;
}

export function FormRenderer({
  form,
  value,
  files,
  onChange,
  onFilesChange,
  serverErrors = [],
  onSubmit,
  submitting,
  disabled,
  showSubmit = true,
}: FormRendererProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const f of form.fields) {
      if (f.default_value !== undefined && f.default_value !== null) {
        init[f.key] = f.default_value;
      }
    }
    return init;
  });
  const [internalFiles, setInternalFiles] = React.useState<FileRef[]>([]);

  const payload = isControlled ? value! : internalValue;
  const fileRefs = files ?? internalFiles;

  const setPayload = (next: Record<string, unknown>) => {
    if (isControlled) onChange?.(next);
    else setInternalValue(next);
  };
  const setFiles = (next: FileRef[]) => {
    if (files !== undefined) onFilesChange?.(next);
    else setInternalFiles(next);
  };

  const errorByKey = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of serverErrors) m[e.field_key] = e.message;
    return m;
  }, [serverErrors]);

  const visibleFields = form.fields
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((f) => !f.visible_if || evaluateRule(f.visible_if, payload));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSubmit(payload, fileRefs);
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-5">
      {visibleFields.map((field) => {
        const span =
          field.grid_cols === 1
            ? "sm:col-span-1"
            : field.grid_cols === 2
              ? "sm:col-span-2"
              : "sm:col-span-2";
        const isDisplayOnly =
          field.field_type === "section_header" || field.field_type === "help_text";
        return (
          <div key={field.key} className={cn("grid gap-1.5", span)}>
            {!isDisplayOnly && (
              <Label
                htmlFor={`f-${field.key}`}
                className="text-muted-foreground text-xs uppercase tracking-wide"
              >
                {field.label}
                {field.validation?.required && <span className="text-destructive ml-0.5">*</span>}
              </Label>
            )}
            <FieldRenderer
              field={field}
              value={payload[field.key]}
              files={fileRefs}
              onChange={(val) => setPayload({ ...payload, [field.key]: val })}
              onFilesChange={setFiles}
              error={errorByKey[field.key] ?? null}
              disabled={disabled}
            />
            {field.help_text && !isDisplayOnly && (
              <p className="text-muted-foreground text-xs">{field.help_text}</p>
            )}
            {errorByKey[field.key] && (
              <p className="text-destructive text-xs">{errorByKey[field.key]}</p>
            )}
          </div>
        );
      })}
      {showSubmit && (
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={submitting || disabled}>
            {submitting ? "Submitting..." : form.submit_button_label}
          </Button>
        </div>
      )}
    </form>
  );
}
